
import { makeAutoObservable, runInAction, makeObservable, observable, action } from 'mobx';
import { authService } from '../services/authService';
import { RootStore } from './RootStore';
import axios from 'axios';
import { API } from '../constants';

export class AuthStore {

    user: any | null = null;
    token: string | null = null;
    refreshToken: string | null = null;
    loading = false;
    error: string | null = null;
    isAuthenticated = false;
    isLoggingOut = false;
    getUserRole = false;
    hasRole = false;
    isAdmin = false;
    getUserFullName = false;

    private rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this, {
            user: observable,
            token: observable,
            refreshToken: observable,
            loading: observable,
            error: observable,
            isAuthenticated: observable,
            isLoggingOut: observable,
            login: action,
            logout: action,
            finishLogout: action,
            clearError: action,
            getUserRole: false,
            hasRole: false,
            isAdmin: false,
            getUserFullName: false
        });
        this.initAuth();
        this.setupAxiosInterceptors();
    }

    initAuth() {

        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');
        const userStr = localStorage.getItem('user');

        if (token && refreshToken && userStr) {
            try {
                const user = JSON.parse(userStr);
                runInAction(() => {
                    this.token = token;
                    this.refreshToken = refreshToken;
                    this.user = user;
                    this.isAuthenticated = true;
                });
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            } catch (e) {
                console.error('Error al analizar datos de usuario:', e);
                this.clearAuthData();
            }
        }
    }

    setupAxiosInterceptors() {
        axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        let refreshAttempts = 0;
        const MAX_REFRESH_ATTEMPTS = 1;

        axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry && refreshAttempts < MAX_REFRESH_ATTEMPTS) {
                    originalRequest._retry = true;
                    refreshAttempts++;

                    try {
                        const refreshToken = localStorage.getItem('refreshToken');
                        if (!refreshToken) {
                            await this.clearAuthData();
                            return Promise.reject(error);
                        }

                        console.log('Intentando refresh token...');

                        const response = await axios.post(`${API.BASE_URL.DEVELOPMENT}/auth/token/refresh/`, {
                            refresh: refreshToken
                        });

                        if (response.data && response.data.access) {
                            const newToken = response.data.access;
                            localStorage.setItem('token', newToken);

                            runInAction(() => {
                                this.token = newToken;
                            });

                            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;

                            refreshAttempts = 0;
                            return axios(originalRequest);
                        } else {
                            throw new Error('Formato de respuesta de refresh inválido');
                        }
                    } catch (refreshError) {
                        console.error('Error en refresh token:', refreshError);
                        this.clearAuthData();
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    async login(username: string, password: string) {
        this.loading = true;
        this.error = null;

        try {
            const response = await authService.login(username, password);

            localStorage.setItem('token', response.access);
            localStorage.setItem('refreshToken', response.refresh);
            localStorage.setItem('user', JSON.stringify(response.user));

            axios.defaults.headers.common['Authorization'] = `Bearer ${response.access}`;

            runInAction(() => {
                this.token = response.access;
                this.refreshToken = response.refresh;
                this.user = response.user;
                this.isAuthenticated = true;
                this.loading = false;
            });

            return true;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.detail || 'Error al iniciar sesión';
                this.loading = false;
            });
            return false;
        }
    }

    async logout() {
        runInAction(() => {
            this.isLoggingOut = true;
        });

        try {
            const token = this.token || localStorage.getItem('token');
            const refreshToken = this.refreshToken || localStorage.getItem('refreshToken');

            if (token && refreshToken) {
                try {
                    await axios.post(
                        `${API.BASE_URL.DEVELOPMENT}/auth/logout/`,
                        { refresh: refreshToken },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                } catch (e) {
                    console.warn('Error en la petición de logout, continuando con la limpieza local');
                }
            }
        } catch (error) {
            console.error('Error durante el logout:', error);
        } finally {
            this.clearAuthData();
        }
    }

    clearAuthData() {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        delete axios.defaults.headers.common['Authorization'];

        runInAction(() => {
            this.user = null;
            this.token = null;
            this.refreshToken = null;
            this.isAuthenticated = false;
            this.isLoggingOut = false;
        });
    }
    
    finishLogout() {
        runInAction(() => {
            this.isLoggingOut = false;
        });
    }

    clearError() {
        this.error = null;
    }
}